import { Component, Input, OnChanges, OnInit, inject } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JobListSchema } from '@app/schemas/jobs';
import { MATCH_DATA_ALIGNMENT } from '@app/utils/find-job';
import { CATEGORY_ICON_MAP_DETAIL, CATEGORY_LABEL_MAP } from '@app/utils/find-job';
import { analytics } from '@app/events/segment/analytics';
import { Store } from '@ngrx/store';
import { State } from '@app/state/reducers';
import { selectGeneratedJobIds } from '@app/state/selectors/job.selector';
import { addGeneratedJobId } from '@app/state/actions/jobs.action';


@Component({
  selector: 'app-match-report',
  imports: [
    MatTooltipModule
  ],
  templateUrl: './match-report.component.html',
  styleUrl: './match-report.component.css'
})
export class MatchReportComponent implements OnChanges {

  @Input() job: JobListSchema = {} as JobListSchema;

  public readonly matchDataAlignment = MATCH_DATA_ALIGNMENT;
  
  public showSkills: boolean = false;
  public showSkillGaps: boolean = false;
  public showMatchScore: boolean = false;
  public toolTipText = 'Your match summary shows how well a job posting fits you based on your resume.';
  private store = inject(Store<State>);
  private generatedJobIds: string[] = [];
  private skillsTimer: ReturnType<typeof setTimeout> | null = null;
  private skillGapsTimer: ReturnType<typeof setTimeout> | null = null;
  private matchScoreTimer: ReturnType<typeof setTimeout> | null = null;
  private btnMarginTimer: ReturnType<typeof setTimeout> | null = null;
  public loading: boolean = false;
  public showBtnMargin: boolean = false;

  constructor() {
    this.store.select(selectGeneratedJobIds).subscribe(ids => {
      this.generatedJobIds = ids;
    });
  }

  private normalizeCategory(): string {
    return this.job?.score_category?.trim()?.toLowerCase().split(" ").slice(0, -1).join("_") || '';
  }

  ngOnChanges(changes: any) {
    if (changes['job']) {
      this.clearPendingTimers();
      const hasGenerated = this.generatedJobIds.includes(this.job.posting_id);
      this.showMatchScore = hasGenerated;
      this.showSkills = hasGenerated;
      this.showSkillGaps = hasGenerated;
      this.loading = false;
      this.showBtnMargin = false;
    }
  }

  private clearPendingTimers() {
    if (this.skillsTimer) { clearTimeout(this.skillsTimer); this.skillsTimer = null; }
    if (this.skillGapsTimer) { clearTimeout(this.skillGapsTimer); this.skillGapsTimer = null; }
    if (this.matchScoreTimer) { clearTimeout(this.matchScoreTimer); this.matchScoreTimer = null; }
  }

  calculateDetailedMatchScore(): string {
    return `${Math.ceil(this.job.match_score * 100)}%`;
  } 

  getSkillCategory() {
    const key = this.normalizeCategory();
    return CATEGORY_LABEL_MAP[key] ?? '';
  }

  getSkillCategoryIcon() {
    const key = this.normalizeCategory();
    return CATEGORY_ICON_MAP_DETAIL[key] ?? '';
  }

  getMatchSummary(key: string) {
    if(Object.keys(this.job?.match_summary || {}).length === 0) {
      return 1
    }
    const matchObj = {
      skills: this.job?.match_summary['skills_category'],
      work_history: this.job?.match_summary['work_history_category'],
      education: this.job?.match_summary['education_category'],
    };

    const matchSummaryScore = {
      very_high: 4,
      high: 3,
      medium: 2,
      low: 1,
    };

    type MatchSummaryKey = keyof typeof matchSummaryScore;

    let matchValue: string = matchObj[key as keyof typeof matchObj] || '';
    const normalizedValue = matchValue.toLowerCase()?.trim()?.split(" ")?.join("_") as MatchSummaryKey;
    return matchSummaryScore[normalizedValue] || 1;
  
  }

  generateRecommendations() {
    this.loading = true;
    // Track Job details generation event
    analytics.track('Job Details Generate Report', {
      section: 'job',
      job_id: this.job.posting_id,
      job_title: this.job.job_title,
      employer_name: this.job.employer_name || null,
    });

    // to track if report is already generated for this particular job 
    this.store.dispatch(addGeneratedJobId({ jobId: this.job.posting_id }));
    
    this.skillsTimer = setTimeout(() => {
      this.showBtnMargin = true;
      this.showSkills = true;
      this.skillGapsTimer = setTimeout(() => {
        this.showSkillGaps = true;
        this.matchScoreTimer = setTimeout(() => {
          this.showMatchScore = true;
          this.loading = false;
          this.showBtnMargin = false;
        }, 1500);
      }, 1500);
    }, 1500);
  }

  trackMatchSummaryInfo() {
    // Track Match Summary Info tooltip event
    analytics.track('Job Details Match Hover', { section: 'job' });
  }
}
